import type { LatLng, MapAdapter } from "../../home/stores/MapAdapter";
import { createMapAdapter } from "../../home/stores/MapAdapterFactory";

interface MapSettingsPreviewConfig {
    provider: string;
    providerLabel: string;
    scriptUrl: string;
    geocodeUrl: string;
    globalPinUrl: string;
    fallbackCenter: LatLng;
    useCurrentLocation?: boolean;
    targetLatId?: string;
    targetLngId?: string;
}

interface MountedPreview {
    adapter: MapAdapter;
    config: MapSettingsPreviewConfig;
}

interface SearchResult {
    position: LatLng;
    label: string;
}

type SearchType = "address" | "keyword" | "latlng";
type ConnectionState = "idle" | "pending" | "success" | "error" | "skipped";

interface ConnectionDiagnostics {
    sdk?: { ok?: boolean; message?: string };
    rest?: { status?: string; ok?: boolean | null; message?: string; code?: string; http_code?: number | null };
}

interface ConnectionTestResponse {
    result?: string;
    msg?: string;
    diagnostics?: ConnectionDiagnostics;
}

type MapPreviewWindow = Window & {
    naver?: any;
    kakao?: any;
    navermap_authFailure?: () => void;
};

const DEFAULT_CENTER: LatLng = { lat: 37.5665, lng: 126.978 };
const PREVIEW_MARKER_ID = "settings-preview-current";

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function setStatus(root: HTMLElement, message: string, state: "idle" | "success" | "error" = "idle"): void {
    const status = root.querySelector<HTMLElement>("[data-map-preview-status]");
    if (!status) return;
    status.textContent = message;
    status.setAttribute("data-state", state);
}

function readConnectionMessage(data: unknown, fallback: string): string {
    if (!data || typeof data !== "object") return fallback;
    const record = data as Record<string, unknown>;
    const message = record.msg ?? record.message;
    return typeof message === "string" && message.trim() !== "" ? message : fallback;
}

function setConnectionRow(row: HTMLElement | null, state: ConnectionState, message: string): void {
    if (!row) return;
    const badge = row.querySelector<HTMLElement>(".admin-stores-settings__test-badge");
    const text = row.querySelector<HTMLElement>("span:last-child");
    const label = state === "success" ? "성공"
        : state === "error" ? "실패"
            : state === "pending" ? "확인중"
                : state === "skipped" ? "건너뜀"
                    : "대기";
    if (badge) {
        badge.textContent = label;
        badge.setAttribute("data-state", state);
    }
    if (text) text.textContent = message;
}

function setBrowserConnectionStatus(state: ConnectionState, message: string): void {
    setConnectionRow(document.querySelector<HTMLElement>("[data-map-connection-browser]"), state, message);
}

function csrfFromForm(form: HTMLFormElement): string {
    return form.querySelector<HTMLInputElement>('input[name="_token"]')?.value || "";
}

function initMapRequestDiagnostics(): void {
    const root = document.querySelector<HTMLElement>("[data-map-request-diagnostics]");
    if (!root) return;

    const key = root.dataset.naverKey || "";
    const authUrlEl = root.querySelector<HTMLElement>("[data-map-debug-auth-url]");
    if (!authUrlEl) return;

    if (key === "") {
        authUrlEl.textContent = "Client ID/API Key ID 저장 후 생성";
        return;
    }

    const params = new URLSearchParams({
        ncpKeyId: key,
        url: window.location.href,
        time: String(Date.now()),
        callback: "__naver_maps_callback__0",
    });
    authUrlEl.textContent = `http://oapi.map.naver.com/v3/auth?${params.toString()}`;
}

function normalizeConnectionState(ok: boolean | null | undefined, status?: string): ConnectionState {
    if (ok === true) return "success";
    if (ok === false) return "error";
    if (status === "skipped" || status === "not_applicable") return "skipped";
    return "idle";
}

async function runConnectionTest(form: HTMLFormElement, root: HTMLElement, silent = false): Promise<void> {
    const sdkRow = root.querySelector<HTMLElement>("[data-map-connection-sdk]");
    const restRow = root.querySelector<HTMLElement>("[data-map-connection-rest]");
    const browserRow = root.querySelector<HTMLElement>("[data-map-connection-browser]");
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');

    if (button) button.disabled = true;
    setConnectionRow(sdkRow, "pending", "저장된 지도 JS SDK 키를 확인하는 중입니다.");
    setConnectionRow(restRow, "pending", "서버 REST Geocoding 테스트를 확인하는 중입니다.");
    if (!silent) {
        setConnectionRow(browserRow, "idle", "지도 표시와 미리보기 검색은 아래 지도 결과를 기준으로 확인합니다.");
    }

    try {
        const response = await fetch(form.action, {
            method: "POST",
            body: new FormData(form),
            headers: {
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
                "X-CSRF-TOKEN": csrfFromForm(form),
            },
        });
        const data = await response.json() as ConnectionTestResponse;
        const diagnostics = data.diagnostics || {};

        const sdkOk = diagnostics.sdk?.ok;
        setConnectionRow(
            sdkRow,
            normalizeConnectionState(sdkOk),
            diagnostics.sdk?.message || (response.ok ? "지도 JS SDK 서버 연결 성공" : readConnectionMessage(data, "지도 JS SDK 서버 연결 실패")),
        );

        const rest = diagnostics.rest;
        setConnectionRow(
            restRow,
            normalizeConnectionState(rest?.ok, rest?.status),
            rest?.message || readConnectionMessage(data, "REST Geocoding 상태를 확인하지 못했습니다."),
        );

        const browserMessage = sdkOk === false
            ? "지도 JS SDK 파일 요청이 실패했습니다. 브라우저 미리보기 인증도 실패할 가능성이 높습니다."
            : "브라우저 Dynamic Map 인증은 아래 지도 미리보기 결과로 확인합니다.";
        setConnectionRow(browserRow, sdkOk === false ? "error" : "idle", browserMessage);
    } catch {
        setConnectionRow(sdkRow, "error", "연동 테스트 요청에 실패했습니다.");
        setConnectionRow(restRow, "error", "서버 응답을 확인하지 못했습니다.");
        setConnectionRow(browserRow, "idle", "아래 미리보기 지도 렌더링과 검색으로 브라우저 SDK 상태를 확인하세요.");
    } finally {
        if (button) button.disabled = false;
    }
}

function readConfig(root: HTMLElement): MapSettingsPreviewConfig | null {
    const configEl = root.querySelector<HTMLScriptElement>("[data-map-preview-config]");
    if (!configEl) return null;

    try {
        const parsed = JSON.parse(configEl.textContent || "{}") as Partial<MapSettingsPreviewConfig>;
        return {
            provider: parsed.provider || "naver",
            providerLabel: parsed.providerLabel || "지도",
            scriptUrl: parsed.scriptUrl || "",
            geocodeUrl: parsed.geocodeUrl || "",
            globalPinUrl: parsed.globalPinUrl || "",
            fallbackCenter: parsed.fallbackCenter || DEFAULT_CENTER,
            useCurrentLocation: parsed.useCurrentLocation !== false,
            targetLatId: typeof parsed.targetLatId === "string" ? parsed.targetLatId : "",
            targetLngId: typeof parsed.targetLngId === "string" ? parsed.targetLngId : "",
        };
    } catch {
        return null;
    }
}

function browserSdkReady(provider: string): boolean {
    const w = window as MapPreviewWindow;
    if (provider === "kakao") {
        return typeof w.kakao?.maps?.services !== "undefined";
    }
    return typeof w.naver?.maps?.Service?.geocode === "function";
}

function loadScriptOnce(url: string, provider: string): Promise<void> {
    if (browserSdkReady(provider)) {
        return Promise.resolve();
    }

    const existing = Array.from(document.querySelectorAll<HTMLScriptElement>("script[data-map-sdk-src]"))
        .find((script) => script.dataset.mapSdkSrc === url);
    if (existing) {
        if (existing.dataset.mapSdkReady === "1") {
            return Promise.resolve();
        }
        return new Promise<void>((resolve, reject) => {
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener("error", () => reject(new Error("지도 SDK 스크립트 로드 실패")), { once: true });
        });
    }

    return new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = url;
        script.async = true;
        script.defer = true;
        script.dataset.mapSdkSrc = url;
        script.onload = () => {
            script.dataset.mapSdkReady = "1";
            resolve();
        };
        script.onerror = () => reject(new Error("지도 SDK 스크립트 로드 실패"));
        document.head.appendChild(script);
    });
}

function getCurrentPosition(fallbackCenter: LatLng): Promise<{ center: LatLng; fallback: boolean }> {
    return new Promise((resolve) => {
        if (!navigator.geolocation) {
            resolve({ center: fallbackCenter, fallback: true });
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    center: {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    },
                    fallback: false,
                });
            },
            () => resolve({ center: fallbackCenter, fallback: true }),
            {
                enableHighAccuracy: true,
                maximumAge: 60000,
                timeout: 7000,
            },
        );
    });
}

function setLatLngFields(root: HTMLElement, position: LatLng): void {
    const lat = root.querySelector<HTMLInputElement>("[data-map-preview-lat]");
    const lng = root.querySelector<HTMLInputElement>("[data-map-preview-lng]");
    if (lat) lat.value = position.lat.toFixed(7);
    if (lng) lng.value = position.lng.toFixed(7);

    const config = readConfig(root);
    if (config?.targetLatId) {
        const targetLat = document.getElementById(config.targetLatId) as HTMLInputElement | null;
        if (targetLat) targetLat.value = position.lat.toFixed(7);
    }
    if (config?.targetLngId) {
        const targetLng = document.getElementById(config.targetLngId) as HTMLInputElement | null;
        if (targetLng) targetLng.value = position.lng.toFixed(7);
    }
}

function readSearchType(root: HTMLElement): SearchType {
    const checked = root.querySelector<HTMLInputElement>('input[name="map_preview_search_type"]:checked');
    const value = checked?.value;
    return value === "keyword" || value === "latlng" ? value : "address";
}

function updateSearchInputs(root: HTMLElement, provider: string): void {
    const type = readSearchType(root);
    const query = root.querySelector<HTMLInputElement>("[data-map-preview-search-input]");
    if (!query) return;

    query.disabled = type === "latlng";
    if (type === "latlng") {
        query.placeholder = "위도와 경도를 입력한 뒤 검색하세요";
        return;
    }
    if (type === "keyword") {
        query.placeholder = provider === "naver"
            ? "네이버 미리보기는 주소 검색을 사용하세요"
            : "장소명 또는 키워드를 입력하세요";
        return;
    }
    query.placeholder = "주소를 입력하세요";
}

function readLatLngSearch(root: HTMLElement): SearchResult | null {
    const latInput = root.querySelector<HTMLInputElement>("[data-map-preview-lat]");
    const lngInput = root.querySelector<HTMLInputElement>("[data-map-preview-lng]");
    const lat = Number(latInput?.value);
    const lng = Number(lngInput?.value);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return null;
    }
    return {
        position: { lat, lng },
        label: `좌표 ${lat.toFixed(7)}, ${lng.toFixed(7)}`,
    };
}

function movePreviewTo(root: HTMLElement, mounted: MountedPreview, result: SearchResult): void {
    mounted.adapter.setMarkerPosition(PREVIEW_MARKER_ID, result.position);
    mounted.adapter.panTo(result.position);
    mounted.adapter.setZoom(15);
    setLatLngFields(root, result.position);
    setStatus(root, `${mounted.config.providerLabel} 검색 결과로 이동했습니다: ${result.label}`, "success");
}

function csrfToken(): string {
    return document.querySelector<HTMLInputElement>('input[name="_token"]')?.value || "";
}

async function searchNaverAddressByServer(query: string, config: MapSettingsPreviewConfig): Promise<SearchResult> {
    if (!config.geocodeUrl) {
        throw new Error("Naver Geocoding REST 검색 URL이 설정되지 않았습니다.");
    }

    const body = new FormData();
    body.append("query", query);
    const token = csrfToken();
    if (token !== "") body.append("_token", token);

    const response = await fetch(config.geocodeUrl, {
        method: "POST",
        body,
        headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-TOKEN": token,
        },
    });
    const data = await response.json() as Record<string, unknown>;
    if (data.result !== "success") {
        throw new Error(String(data.msg || "Naver Geocoding REST 검색에 실패했습니다."));
    }

    const lat = Number(data.lat);
    const lng = Number(data.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        throw new Error("Naver Geocoding REST 검색 좌표를 읽을 수 없습니다.");
    }

    return {
        position: { lat, lng },
        label: String(data.label || query),
    };
}

function searchNaverAddressByBrowser(query: string): Promise<SearchResult> {
    const w = window as MapPreviewWindow;
    const service = w.naver?.maps?.Service;
    if (!service?.geocode) {
        return Promise.reject(new Error("네이버 지도 Geocoder 모듈을 사용할 수 없습니다."));
    }

    return new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => {
            reject(new Error("네이버 Geocoding 브라우저 검색이 응답하지 않습니다. Geocoding 상품/권한 또는 서비스 URL에 현재 페이지 URL이 허용됐는지 확인하세요."));
        }, 7000);
        service.geocode({ query }, (status: string, response: any) => {
            window.clearTimeout(timeout);
            if (status !== service.Status.OK) {
                reject(new Error("네이버 주소 검색에 실패했습니다. Geocoding 상품/권한 또는 서비스 URL 제한으로 geocode-js가 차단됐을 수 있습니다."));
                return;
            }

            const item = response?.v2?.addresses?.[0];
            const lat = Number(item?.y);
            const lng = Number(item?.x);
            if (!item || !Number.isFinite(lat) || !Number.isFinite(lng)) {
                reject(new Error("검색 결과가 없습니다."));
                return;
            }

            resolve({
                position: { lat, lng },
                label: item.roadAddress || item.jibunAddress || query,
            });
        });
    });
}

function searchNaverAddress(query: string, config: MapSettingsPreviewConfig): Promise<SearchResult> {
    if (config.geocodeUrl) {
        return searchNaverAddressByServer(query, config);
    }
    return searchNaverAddressByBrowser(query);
}

function searchKakaoAddress(query: string): Promise<SearchResult> {
    const w = window as MapPreviewWindow;
    const geocoder = new w.kakao.maps.services.Geocoder();

    return new Promise((resolve, reject) => {
        geocoder.addressSearch(query, (results: any[], status: string) => {
            if (status !== w.kakao.maps.services.Status.OK || !results?.length) {
                reject(new Error("카카오 주소 검색 결과가 없습니다."));
                return;
            }

            const item = results[0];
            const lat = Number(item.y);
            const lng = Number(item.x);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                reject(new Error("카카오 주소 검색 좌표를 읽을 수 없습니다."));
                return;
            }

            resolve({
                position: { lat, lng },
                label: item.road_address?.address_name || item.address_name || query,
            });
        });
    });
}

function searchKakaoKeyword(query: string): Promise<SearchResult> {
    const w = window as MapPreviewWindow;
    const places = new w.kakao.maps.services.Places();

    return new Promise((resolve, reject) => {
        places.keywordSearch(query, (results: any[], status: string) => {
            if (status !== w.kakao.maps.services.Status.OK || !results?.length) {
                reject(new Error("카카오 키워드 검색 결과가 없습니다."));
                return;
            }

            const item = results[0];
            const lat = Number(item.y);
            const lng = Number(item.x);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                reject(new Error("카카오 키워드 검색 좌표를 읽을 수 없습니다."));
                return;
            }

            resolve({
                position: { lat, lng },
                label: item.place_name || item.address_name || query,
            });
        });
    });
}

async function runSearch(root: HTMLElement, mounted: MountedPreview): Promise<void> {
    const type = readSearchType(root);
    if (type === "latlng") {
        const result = readLatLngSearch(root);
        if (!result) {
            setStatus(root, "위도는 -90~90, 경도는 -180~180 범위의 숫자로 입력하세요.", "error");
            return;
        }
        movePreviewTo(root, mounted, result);
        return;
    }

    const input = root.querySelector<HTMLInputElement>("[data-map-preview-search-input]");
    const query = (input?.value || "").trim();
    if (query === "") {
        setStatus(root, "검색어를 입력하세요.", "error");
        return;
    }

    if (mounted.config.provider === "naver" && type === "keyword") {
        setStatus(root, "네이버 미리보기 검색은 주소 검색만 지원합니다. 키워드는 카카오 지도에서 테스트할 수 있습니다.", "error");
        return;
    }

    setStatus(root, `${mounted.config.providerLabel} ${type === "keyword" ? "키워드" : "주소"} 검색 중입니다.`);
    const result = mounted.config.provider === "kakao"
        ? (type === "keyword" ? await searchKakaoKeyword(query) : await searchKakaoAddress(query))
        : await searchNaverAddress(query, mounted.config);
    movePreviewTo(root, mounted, result);
}

async function mountPreview(root: HTMLElement, config: MapSettingsPreviewConfig): Promise<MountedPreview | null> {
    const canvas = root.querySelector<HTMLElement>("[data-map-preview-canvas]");
    if (!canvas) return null;

    if (!config.scriptUrl) {
        setStatus(root, "저장된 지도 API 키가 없어 미리보기를 표시할 수 없습니다.", "error");
        return null;
    }

    let naverAuthFailed = false;
    if (config.provider === "naver") {
        const w = window as MapPreviewWindow;
        const previousAuthFailure = w.navermap_authFailure;
        w.navermap_authFailure = () => {
            naverAuthFailed = true;
            const origin = window.location.origin;
            setStatus(
                root,
                `네이버 지도 JS SDK 인증 실패입니다. Client ID/API Key ID, Dynamic Map 선택 여부, Maps 서비스 URL의 ${origin} 등록 상태를 확인하세요.`,
                "error",
            );
            setBrowserConnectionStatus(
                "error",
                `네이버 Dynamic Map 브라우저 인증 실패입니다. Maps 서비스 URL에 ${origin} 등록과 Dynamic Map 선택 여부를 확인하세요.`,
            );
            if (previousAuthFailure) previousAuthFailure();
        };
    }

    setStatus(
        root,
        config.useCurrentLocation === false
            ? "저장된 좌표 기준으로 지도를 표시합니다."
            : "현재 위치 권한을 요청하는 중입니다. 권한을 거부하면 기본 좌표로 미리보기를 표시합니다.",
    );
    const position = config.useCurrentLocation === false
        ? { center: config.fallbackCenter || DEFAULT_CENTER, fallback: false }
        : await getCurrentPosition(config.fallbackCenter || DEFAULT_CENTER);
    setStatus(root, `${config.providerLabel} SDK를 불러오는 중입니다.`);

    await loadScriptOnce(config.scriptUrl, config.provider);

    canvas.innerHTML = "";
    const adapter = createMapAdapter(config.provider);
    await adapter.init({
        container: canvas,
        center: position.center,
        zoom: 15,
    });
    if (config.provider === "naver") {
        await delay(300);
        if (naverAuthFailed) {
            return null;
        }
    }
    adapter.addMarker(PREVIEW_MARKER_ID, {
        position: position.center,
        title: position.fallback ? "기본 위치" : "현재 위치",
        pin: config.globalPinUrl ? { url: config.globalPinUrl, width: 40, height: 40 } : undefined,
    });
    adapter.panTo(position.center);
    setLatLngFields(root, position.center);
    updateSearchInputs(root, config.provider);

    setStatus(
        root,
        position.fallback
            ? `${config.providerLabel} 지도가 표시되었습니다. 현재 위치 권한이 없어 기본 좌표를 사용했습니다.`
            : `${config.providerLabel} 지도가 현재 위치 기준으로 표시되었습니다.`,
        "success",
    );
    setBrowserConnectionStatus("success", `${config.providerLabel} 브라우저 지도 미리보기 인증이 성공했습니다.`);

    return { adapter, config };
}

export function initMapSettingsPreview(): void {
    const root = document.querySelector<HTMLElement>("[data-map-settings-preview]");
    if (!root) return;

    const config = readConfig(root);
    if (!config) {
        setStatus(root, "지도 미리보기 설정을 읽을 수 없습니다.", "error");
        return;
    }

    let running = false;
    let searchRunning = false;
    let mounted: MountedPreview | null = null;
    const run = (): void => {
        if (running) return;
        running = true;
        mountPreview(root, config)
            .then((nextMounted) => {
                if (nextMounted) mounted = nextMounted;
            })
            .catch(() => {
                const message = config.provider === "naver"
                    ? "지도를 불러오지 못했습니다. 네이버 지도 JS SDK 도메인 등록과 Client ID를 확인하세요."
                    : "지도를 불러오지 못했습니다. 카카오 Web 플랫폼 도메인 등록과 JavaScript 앱 키를 확인하세요.";
                setStatus(root, message, "error");
                setBrowserConnectionStatus("error", message);
            })
            .finally(() => {
                running = false;
            });
    };

    root.querySelector<HTMLElement>("[data-map-preview-refresh]")?.addEventListener("click", run);
    root.querySelectorAll<HTMLInputElement>('input[name="map_preview_search_type"]').forEach((radio) => {
        radio.addEventListener("change", () => updateSearchInputs(root, config.provider));
    });
    root.querySelector<HTMLElement>("[data-map-preview-search-btn]")?.addEventListener("click", () => {
        if (searchRunning) return;
        if (!mounted) {
            setStatus(root, "지도가 준비된 뒤 검색할 수 있습니다.", "error");
            return;
        }
        const button = root.querySelector<HTMLButtonElement>("[data-map-preview-search-btn]");
        searchRunning = true;
        if (button) button.disabled = true;
        runSearch(root, mounted)
            .catch((error: Error) => {
                setStatus(root, error.message || "검색에 실패했습니다.", "error");
            })
            .finally(() => {
                searchRunning = false;
                if (button) button.disabled = false;
            });
    });
    root.querySelector<HTMLInputElement>("[data-map-preview-search-input]")?.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        root.querySelector<HTMLButtonElement>("[data-map-preview-search-btn]")?.click();
    });
    run();
}

export function initMapConnectionStatus(): void {
    const form = document.querySelector<HTMLFormElement>("[data-map-connection-test-form]");
    const root = document.querySelector<HTMLElement>("[data-map-connection-status]");
    initMapRequestDiagnostics();
    if (!form || !root) return;

    form.addEventListener("submit", (event) => {
        event.preventDefault();
        void runConnectionTest(form, root, false);
    });

    void runConnectionTest(form, root, true);
}

export function initMapProviderSettingsToggle(): void {
    const presetsEl = document.querySelector<HTMLScriptElement>("[data-map-provider-presets]");
    const providerEls = document.querySelectorAll<HTMLInputElement>('[data-map-providers] input[name="map_provider"]');
    const sections = document.querySelectorAll<HTMLElement>("[data-map-section]");
    const activeLabel = document.getElementById("map-test-active-label");
    if (!providerEls.length || !sections.length) return;

    let presets: Record<string, { label: string }> = {};
    try {
        presets = presetsEl ? JSON.parse(presetsEl.textContent || "{}") as Record<string, { label: string }> : {};
    } catch {
        presets = {};
    }

    const apply = (key: string): void => {
        sections.forEach((section) => {
            section.hidden = section.getAttribute("data-map-section") !== key;
        });
        if (activeLabel && presets[key]) {
            activeLabel.textContent = presets[key].label;
        }
    };

    providerEls.forEach((el) => {
        el.addEventListener("change", () => {
            if (el.checked) apply(el.value);
        });
        if (el.checked) apply(el.value);
    });
}
